import { useReducedMotion } from 'motion/react';
import type { FC, ReactNode, Ref } from 'react';
import { useLayoutEffect, useRef } from 'react';

const headerMarqueeSpeedPxPerSecond = 56;
type TableHeaderLabelProps = {
  children: ReactNode;
  /**
   * Rendered as a small caption above the header label. Usage is highly discouraged,
   * unless there is a very specific need for it.
   */
  colGroup?: ReactNode;
  ref?: Ref<HTMLSpanElement>;
};

export const TableHeaderLabel: FC<TableHeaderLabelProps> = ({
  children,
  colGroup,
  ref,
}) => {
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const textRef = useRef<HTMLSpanElement | null>(null);
  const prefersReducedMotion = useReducedMotion();

  useLayoutEffect(() => {
    const container = containerRef.current;
    const text = textRef.current;
    if (!container || !text) return;

    // Sortable headers are already focusable, since they're inside a button
    const isAlreadyFocusable = container.closest('button') !== null;

    const cell = container.closest('th');

    const updateMarqueeStyle = (): void => {
      // measure against the content box, so the text stops where the right padding starts
      const { paddingLeft, paddingRight } = getComputedStyle(container);
      const contentWidth =
        container.clientWidth -
        parseFloat(paddingLeft) -
        parseFloat(paddingRight);
      const travelDistance = Math.max(0, text.offsetWidth - contentWidth);
      const durationSeconds = travelDistance / headerMarqueeSpeedPxPerSecond;

      text.style.setProperty(
        '--header-marquee-duration',
        `${durationSeconds}s`
      );
      text.style.setProperty(
        '--header-marquee-translate',
        `-${travelDistance}px`
      );

      const isTruncated = travelDistance > 0;
      container.toggleAttribute('data-header-marquee-truncated', isTruncated);

      // only give the label its own tabindex if it's not already focusable
      if (isTruncated && !isAlreadyFocusable) {
        container.setAttribute('tabindex', '0');
      } else {
        container.removeAttribute('tabindex');
      }

      // show native tooltip on hover if reduced motion is active and the label is overflowing
      if (prefersReducedMotion && travelDistance > 0) {
        container.title = text.textContent;
      } else {
        container.removeAttribute('title');
      }
    };

    updateMarqueeStyle();

    const observer = new ResizeObserver(updateMarqueeStyle);
    observer.observe(container);
    observer.observe(text);

    // Clicking a header focuses it, which would keep
    // the animation running after the mouse leaves
    const handlePointerLeave = (): void => {
      const activeElement = container.ownerDocument.activeElement;
      if (
        activeElement instanceof HTMLElement &&
        cell?.contains(activeElement)
      ) {
        activeElement.blur();
      }
    };

    cell?.addEventListener('pointerleave', handlePointerLeave);

    return () => {
      observer.disconnect();
      cell?.removeEventListener('pointerleave', handlePointerLeave);
    };
  }, [children, prefersReducedMotion]);

  return (
    <>
      {colGroup && (
        <span
          className={`
            block truncate text-label-sm font-label-regular
            text-foreground-surface-subdued
          `}
        >
          {colGroup}
        </span>
      )}
      {/* The cell owns the padding. The label bleeds into it with negative margins, so the marquee clips at the cell edge. */}
      <span
        ref={containerRef}
        className={`
          -mr-(--header-label-pr) -ml-md block min-w-0 overflow-hidden
          pr-(--header-label-pr) pl-md whitespace-nowrap
          [--header-label-pr:var(--spacing-md)]
          in-[button]:[--header-label-pr:0px]
          data-header-marquee-truncated:mask-[linear-gradient(90deg,#000_calc(100%-var(--header-label-pr)-var(--spacing-xl)),transparent_calc(100%-var(--header-label-pr)))]
          group-focus-within/th:data-header-marquee-truncated:mask-none
          group-hover/th:data-header-marquee-truncated:mask-none
        `}
      >
        <span ref={ref} className="inline-block">
          <span
            data-header-marquee-text
            ref={textRef}
            className={`
              inline-block
              motion-safe:translate-x-0
              motion-safe:[transition-property:translate]
              motion-safe:ease-linear
              motion-safe:group-focus-within/th:translate-x-(--header-marquee-translate)
              motion-safe:group-focus-within/th:duration-(--header-marquee-duration)
              motion-safe:group-hover/th:translate-x-(--header-marquee-translate)
              motion-safe:group-hover/th:duration-(--header-marquee-duration)
            `}
          >
            {children}
          </span>
        </span>
      </span>
    </>
  );
};
